import { Box, Button, Flex, HStack, IconButton, Input, Text, VStack } from '@chakra-ui/react'
import { Dispatch, SetStateAction } from 'react'

import { FormField } from '../../../../components/FormField'
import { Icon } from '../../../../components/Icon'
import { MoneyInput } from '../../../../components/MaskedInput'
import { FormSelect } from '../../../../components/Select'
import { IContestFormTabProps } from './types'

interface IProps extends IContestFormTabProps {
  newFeeItem: string
  setNewFeeItem: Dispatch<SetStateAction<string>>
}

export function RegistrationTab({ form, setForm, errors, saving, newFeeItem, setNewFeeItem }: IProps) {
  const addFeeItem = () => {
    if (newFeeItem.trim()) {
      setForm({
        ...form,
        registration_fee_items: [...(form.registration_fee_items || []), newFeeItem.trim()]
      })
      setNewFeeItem('')
    }
  }

  return (
    <Flex direction="column" gap={3}>
      <Flex direction="row" gap={10}>
        <Box>
          <FormField
            label="Valor de inscrição (mín. R$ 5,00 ou R$ 0,00)"
            htmlFor="registration_fee"
            error={errors.registration_fee}
            isRequired
          >
            <MoneyInput
              value={form.registration_fee}
              onChange={(registration_fee) => setForm({ ...form, registration_fee: registration_fee ?? 0 })}
              isDisabled={saving}
            />
          </FormField>
          {form.registration_fee > 0 &&
            (() => {
              const minInstallment = 5
              const maxPossible = Math.min(12, Math.floor(form.registration_fee / minInstallment)) || 1
              const effectiveMax = Math.min(form.max_installments, maxPossible)
              if (effectiveMax < form.max_installments) {
                return (
                  <Text color="orange.400" fontSize="sm" mt={1}>
                    ⚠ O valor mínimo por parcela no Asaas é de R$ 5,00. Com esse valor de inscrição, o parcelamento
                    máximo disponível será de {effectiveMax}x.
                  </Text>
                )
              }
              return null
            })()}
        </Box>

        <Box>
          <FormField label="Quantidade máxima de parcelas" htmlFor="max_installments" error={errors.max_installments}>
            <FormSelect
              id="max_installments"
              isDisabled={saving || form.registration_fee === 0}
              value={form.max_installments}
              onChange={(value) => setForm({ ...form, max_installments: Number(value) })}
              options={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((n) => ({
                value: n,
                label: n === 1 ? '1x (à vista)' : `${n}x`
              }))}
              hideEmptyOption
            />
          </FormField>
          <Text variant="caption" mt={1}>
            De 1 a 12. Juros aplicados a partir de 2x. Parcela mínima: R$ 5,00.
          </Text>
        </Box>

        <Box>
          <FormField
            label="Limite de Inscrições"
            htmlFor="max_registrations"
            error={errors.max_registrations}
            isRequired
          >
            <Input
              value={form.max_registrations}
              onChange={(e) => setForm({ ...form, max_registrations: Number(e.target.value) })}
              id="max_registrations"
              disabled={saving}
              type="number"
            />
          </FormField>
        </Box>
      </Flex>

      <Box>
        <FormField
          label="Itens inclusos na taxa de inscrição"
          htmlFor="registration_fee_items"
          error={errors.registration_fee_items}
        >
          <VStack spacing={2} align="stretch">
            <HStack spacing={2}>
              <Input
                placeholder="Digite um item e pressione Enter"
                value={newFeeItem}
                onChange={(e) => setNewFeeItem(e.target.value)}
                disabled={saving}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newFeeItem.trim()) {
                    e.preventDefault()
                    addFeeItem()
                  }
                }}
                size="sm"
              />
              <Button onClick={addFeeItem} isDisabled={!newFeeItem.trim() || saving} colorScheme="green" size="sm">
                Adicionar
              </Button>
            </HStack>

            <VStack spacing={1} align="stretch">
              {form.registration_fee_items && form.registration_fee_items.length > 0 ? (
                form.registration_fee_items.map((item, index) => (
                  <HStack key={index} justify="space-between" px={3} py={2} bg="gray.50" borderRadius="md">
                    <Text fontSize="sm">{item}</Text>
                    <IconButton
                      icon={<Icon name="lni lni-close" />}
                      size="xs"
                      variant="ghost"
                      aria-label="Remover"
                      onClick={() => {
                        setForm({
                          ...form,
                          registration_fee_items: form.registration_fee_items
                            ? form.registration_fee_items.filter((_, i) => i !== index)
                            : []
                        })
                      }}
                      isDisabled={saving}
                    />
                  </HStack>
                ))
              ) : (
                <Text variant="caption" py={2}>
                  Nenhum item adicionado
                </Text>
              )}
            </VStack>
          </VStack>
        </FormField>
      </Box>
    </Flex>
  )
}
